import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="places-autocomplete"
// Uses PlaceAutocompleteElement (March 2025+ API)
// Sets location_bias as "lat,lng" format for Google Places API
export default class extends Controller {
  static targets = ["input", "locationBias", "container"]
  static values = {
    types: { type: Array, default: ["(cities)"] }
  }

  async connect() {
    console.log("Places autocomplete controller connected")
    await this.initializeAutocomplete()
  }

  async initializeAutocomplete() {
    // Check if Google Maps importLibrary is available
    if (typeof google === "undefined" || typeof google.maps === "undefined" || typeof google.maps.importLibrary !== "function") {
      console.error("Google Maps API not loaded. Make sure Google Places API key is configured in Integrations.")
      this.showFallbackInput()
      return
    }

    try {
      console.log("Loading Google Places library...")
      const places = await google.maps.importLibrary("places")
      console.log("Google Places library loaded, available:", Object.keys(places))

      // Check if we have an existing value (editing vs new)
      const existingValue = this.inputTarget?.value

      if (existingValue) {
        // Editing: Use legacy API which preserves existing input values
        console.log("Existing value detected, using legacy Autocomplete API")
        if (places.Autocomplete) {
          this.setupLegacyAutocomplete(places.Autocomplete)
        } else {
          console.error("Legacy Autocomplete not available")
          this.showFallbackInput()
        }
      } else {
        // New: Use PlaceAutocompleteElement which has better autocomplete
        console.log("No existing value, using PlaceAutocompleteElement")
        if (places.PlaceAutocompleteElement) {
          this.setupNewAutocomplete(places.PlaceAutocompleteElement)
        } else if (places.Autocomplete) {
          // Fallback to legacy if PlaceAutocompleteElement not available
          console.log("PlaceAutocompleteElement not available, using legacy")
          this.setupLegacyAutocomplete(places.Autocomplete)
        } else {
          console.error("No autocomplete API available")
          this.showFallbackInput()
        }
      }
    } catch (error) {
      console.error("Failed to load Google Places library:", error)
      this.showFallbackInput()
    }
  }

  setupNewAutocomplete(PlaceAutocompleteElement) {
    if (!this.hasInputTarget) {
      console.error("No input target found for places autocomplete")
      return
    }

    console.log("Setting up PlaceAutocompleteElement for new search")

    // Create the new autocomplete element
    this.autocompleteElement = new PlaceAutocompleteElement({
      types: this.typesValue
    })

    // Copy styling from original input
    const inputClasses = this.inputTarget.className
    const placeholder = this.inputTarget.placeholder

    // Style the autocomplete element container
    const wrapper = document.createElement("div")
    wrapper.className = "places-autocomplete-wrapper"
    wrapper.style.cssText = "width: 100%;"

    // The PlaceAutocompleteElement creates its own input
    wrapper.appendChild(this.autocompleteElement)

    // Replace our input with the wrapper
    this.inputTarget.parentNode.insertBefore(wrapper, this.inputTarget)
    this.inputTarget.style.display = "none"

    // Apply styling to the shadow DOM input (if accessible)
    this.styleAutocompleteElement(inputClasses)

    // Listen for place selection using gmp-select (new API as of 2025)
    this.autocompleteElement.addEventListener("gmp-select", async (event) => {
      console.log("gmp-select event fired!", event)
      await this.handlePlaceSelect(event)
    })

    console.log("PlaceAutocompleteElement initialized successfully")
  }

  styleAutocompleteElement(inputClasses) {
    // Apply custom styles via CSS
    const style = document.createElement("style")
    style.textContent = `
      .places-autocomplete-wrapper {
        width: 100%;
      }
      gmp-place-autocomplete {
        width: 100%;
      }
      gmp-place-autocomplete input {
        width: 100%;
        padding: 0.5rem 0.75rem;
        border: 1px solid rgb(203 213 225);
        border-radius: 0.375rem;
        font-size: 0.875rem;
        line-height: 1.25rem;
        box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
      }
      gmp-place-autocomplete input:focus {
        outline: none;
        border-color: rgb(99 102 241);
        box-shadow: 0 0 0 1px rgb(99 102 241);
      }
      .dark gmp-place-autocomplete input {
        background-color: rgb(51 65 85);
        border-color: rgb(71 85 105);
        color: rgb(226 232 240);
      }
    `
    document.head.appendChild(style)
  }

  async handlePlaceSelect(event) {
    const placePrediction = event.placePrediction
    if (!placePrediction) {
      console.error("No placePrediction in event:", event)
      return
    }

    // Convert prediction to place (new API as of 2025)
    const place = placePrediction.toPlace()
    console.log("Place selected:", place)

    try {
      // Fetch location details
      await place.fetchFields({ fields: ["location", "formattedAddress", "displayName"] })

      const location = place.location
      if (location) {
        const lat = location.lat()
        const lng = location.lng()

        // Set location_bias as "lat,lng" format
        if (this.hasLocationBiasTarget) {
          this.locationBiasTarget.value = `${lat},${lng}`
          console.log("Location bias set to:", this.locationBiasTarget.value)
        } else {
          console.error("No locationBias target found!")
        }

        // Update the hidden input with the display value
        // Prefer formattedAddress (includes city, state, country) over displayName (just city)
        if (this.hasInputTarget) {
          this.inputTarget.value = place.formattedAddress || place.displayName || ""
          console.log("Location display set to:", this.inputTarget.value)
        }

        // Show visual confirmation that location was captured
        this.showLocationConfirmation(place.formattedAddress || place.displayName)

        // Dispatch custom event with place details
        this.dispatch("selected", {
          detail: {
            place: place,
            latitude: lat,
            longitude: lng,
            name: place.displayName,
            formattedAddress: place.formattedAddress
          }
        })

        console.log("Location set successfully:", lat, lng)
      } else {
        console.error("No location data returned from Google Places")
        this.showLocationError("Could not get location coordinates. Please try selecting again.")
      }
    } catch (error) {
      console.error("Failed to fetch place details:", error)
      this.showLocationError("Failed to get location details. Please try again.")
    }
  }

  showLocationConfirmation(locationName) {
    // Find or create confirmation element
    let confirmation = this.element.querySelector(".location-confirmation")
    if (!confirmation) {
      confirmation = document.createElement("p")
      confirmation.className = "location-confirmation mt-1 text-xs text-green-600 dark:text-green-400"
      // Insert after the wrapper
      const wrapper = this.element.querySelector(".places-autocomplete-wrapper")
      if (wrapper) {
        wrapper.after(confirmation)
      }
    }
    confirmation.textContent = `✓ Location captured: ${locationName}`
  }

  showLocationError(message) {
    let errorEl = this.element.querySelector(".location-error")
    if (!errorEl) {
      errorEl = document.createElement("p")
      errorEl.className = "location-error mt-1 text-xs text-red-500 dark:text-red-400"
      const wrapper = this.element.querySelector(".places-autocomplete-wrapper")
      if (wrapper) {
        wrapper.after(errorEl)
      }
    }
    errorEl.textContent = message
  }

  // Legacy API support for existing Google Cloud customers
  setupLegacyAutocomplete(Autocomplete) {
    if (!this.hasInputTarget) {
      console.error("No input target found for places autocomplete")
      return
    }

    console.log("Setting up legacy autocomplete on input:", this.inputTarget)

    this.autocomplete = new Autocomplete(this.inputTarget, {
      types: this.typesValue
    })

    this.autocomplete.addListener("place_changed", () => this.handleLegacyPlaceSelect())
    console.log("Legacy Autocomplete initialized successfully")
  }

  handleLegacyPlaceSelect() {
    const place = this.autocomplete.getPlace()

    if (!place.geometry) {
      console.warn("No geometry found for place:", place.name)
      return
    }

    const lat = place.geometry.location.lat()
    const lng = place.geometry.location.lng()

    // Set location_bias as "lat,lng" format
    if (this.hasLocationBiasTarget) {
      this.locationBiasTarget.value = `${lat},${lng}`
      console.log("Location bias set to:", this.locationBiasTarget.value)
    }

    // Update the input field with formatted address (for form submission)
    if (this.hasInputTarget && place.formatted_address) {
      this.inputTarget.value = place.formatted_address
      console.log("Location display set to:", place.formatted_address)
    }

    // Dispatch custom event with place details
    this.dispatch("selected", {
      detail: {
        place: place,
        latitude: lat,
        longitude: lng,
        name: place.name,
        formattedAddress: place.formatted_address
      }
    })
  }

  showFallbackInput() {
    // Show a visible error message that autocomplete is not available
    if (this.hasInputTarget) {
      this.inputTarget.placeholder = "Location autocomplete unavailable"

      // Add a visible warning message
      const warning = document.createElement("p")
      warning.className = "mt-1 text-xs text-amber-600 dark:text-amber-400"
      warning.innerHTML = '⚠️ Google Places API not loaded. <a href="/account/integrations" class="underline">Add your API key in Integrations</a> to enable location autocomplete.'
      this.inputTarget.after(warning)
    }
  }

  disconnect() {
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete)
    }
  }
}
